// Set premium flag FIRST, before any other RxDB imports
// The RXDB_PREMIUM license is validated during package installation and build,
// not at runtime, so this is safe to call in all environments
import { setPremiumFlag } from 'rxdb-premium/plugins/shared'

import { addRxPlugin, createRxDatabase } from 'rxdb'
import { getRxStorageWorker } from 'rxdb-premium/plugins/storage-worker'
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv'
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode'
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup'
import { RxDBUpdatePlugin } from 'rxdb/plugins/update'
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder'
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema'
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election'
import { deleteAllRxDBDatabases, handlePendingResets } from '../resetUtils'
import { ReplicationManager } from '../replication/ReplicationManager'
import {
  taskMigrationStrategies,
  taskSchema,
} from './schemas/task'
import {  taskContentMigrationStrategies, taskContentSchema } from './schemas/taskContent'
import {  labelSchema } from './schemas/label'
import {  labelGroupSchema } from './schemas/labelGroup'
import {

  taskLabelSchema
} from './schemas/taskLabel'
import {  commentMigrationStrategies, commentSchema } from './schemas/comment'
import {  reminderSchema } from './schemas/reminder'
import {
  notificationMigrationStrategies,
  notificationSchema,
} from './schemas/notification'
import {  projectMigrationStrategies, projectSchema } from './schemas/project'
import { statusMigrationStrategies, statusSchema } from './schemas/status'
import {  memberMigrationStrategies, memberSchema } from './schemas/member'
import {
  activityMigrationStrategies,
  activitySchema,
} from './schemas/activity'
import { reactionMigrationStrategies, reactionSchema } from './schemas/reaction'
import { checklistMigrationStrategies, checklistSchema } from './schemas/checklist'
import {
  checklistItemMigrationStrategies,
  checklistItemSchema
} from './schemas/checklistItem'
import { pullRequestMigrationStrategies, pullRequestSchema } from './schemas/pullRequest'
import { mentionMigrationStrategies, mentionSchema } from './schemas/mention'
import {  attachmentSchema } from './schemas/attachment'
import { taskRelationSchema } from './schemas/taskRelation'
import { entityLinkSchema } from './schemas/entityLink'
import { pageSchema } from './schemas/page'
import { pageContentSchema } from './schemas/pageContent'
import { subscriptionSchema } from './schemas/subscription'
import { planSchema } from './schemas/plan'
import { usageSchema } from './schemas/usage'
import { workItemTypeMigrationStrategies, workItemTypeSchema } from './schemas/workItemType'
import { savedFilterMigrationStrategies, savedFilterSchema } from './schemas/savedFilter'
import { starredSchema } from './schemas/starred'
import { customFieldDefinitionMigrationStrategies, customFieldDefinitionSchema } from './schemas/customFieldDefinition'
import { customFieldTabMigrationStrategies, customFieldTabSchema } from './schemas/customFieldTab'
import { userPreferencesMigrationStrategies, userPreferencesSchema } from './schemas/userPreferences'
import { customFieldGroupMigrationStrategies, customFieldGroupSchema } from './schemas/customFieldGroup'
import { customFieldValueMigrationStrategies, customFieldValueSchema } from './schemas/customFieldValue'
import { cycleSchema } from './schemas/cycle'
import { cycleWorkItemMigrationStrategies, cycleWorkItemSchema } from './schemas/cycleWorkItem'
import { personalNoteSchema } from './schemas/personalNote'
import { personalNoteContentSchema } from './schemas/personalNoteContent'
import { spaceOrderSchema } from './schemas/spaceOrder'
import { entityWatcherSchema } from './schemas/entityWatcher'
import { documentContributorSchema } from './schemas/documentContributor'
import { setupTaskReplication } from './replication/taskReplication'
import { setupTaskContentReplication } from './replication/taskContentReplication'
import { setupLabelReplication } from './replication/labelReplication'
import { setupLabelGroupReplication } from './replication/labelGroupReplication'
import { setupTaskLabelReplication } from './replication/taskLabelReplication'
import { setupCommentReplication } from './replication/commentReplication'
import { setupReminderReplication } from './replication/reminderReplication'
import { setupNotificationReplication } from './replication/notificationReplication'
import { setupProjectReplication } from './replication/projectReplication'
import { setupStatusReplication } from './replication/statusReplication'
import { setupMemberReplication } from './replication/memberReplication'
import { setupActivityReplication } from './replication/activityReplication'
import { setupReactionReplication } from './replication/reactionReplication'
import { setupChecklistReplication } from './replication/checklistReplication'
import { setupChecklistItemReplication } from './replication/checklistItemReplication'
import { setupPullRequestReplication } from './replication/pullRequestReplication'
import { setupMentionReplication } from './replication/mentionReplication'
import { setupAttachmentReplication } from './replication/attachmentReplication'
import { setupTaskRelationReplication } from './replication/taskRelationReplication'
import { setupEntityLinkReplication } from './replication/entityLinkReplication'
import { setupPageReplication } from './replication/pageReplication'
import { setupPageContentReplication } from './replication/pageContentReplication'
import { setupSubscriptionReplication } from './replication/subscriptionReplication'
import { setupPlanReplication } from './replication/planReplication'
import { setupUsageReplication } from './replication/usageReplication'
import { setupWorkItemTypeReplication } from './replication/workItemTypeReplication'
import { setupSavedFilterReplication } from './replication/savedFilterReplication'
import { setupStarredReplication } from './replication/starredReplication'
import { setupCustomFieldDefinitionReplication } from './replication/customFieldDefinitionReplication'
import { setupCustomFieldTabReplication } from './replication/customFieldTabReplication'
import { setupUserPreferencesReplication } from './replication/userPreferencesReplication'
import { setupCustomFieldGroupReplication } from './replication/customFieldGroupReplication'
import { setupCustomFieldValueReplication } from './replication/customFieldValueReplication'
import { setupCycleReplication } from './replication/cycleReplication'
import { setupCycleWorkItemReplication } from './replication/cycleWorkItemReplication'
import { setupPersonalNoteReplication } from './replication/personalNoteReplication'
import { setupPersonalNoteContentReplication } from './replication/personalNoteContentReplication'
import { setupSpaceOrderReplication } from './replication/spaceOrderReplication'
import { setupEntityWatcherReplication } from './replication/entityWatcherReplication'
import { setupDocumentContributorReplication } from './replication/documentContributorReplication'
import { PurgeService } from './cleanup/purgeService'
import type { PlanDocument, SubscriptionDocument, UsageDocument } from '@/lib/types/subscription'
import type {TaskRelationDocument} from './schemas/taskRelation';
import type {EntityLinkDocument} from './schemas/entityLink';
import type {PageDocument} from './schemas/page';
import type {PageContentDocument} from './schemas/pageContent';
import type {AttachmentDocument} from './schemas/attachment';
import type {MentionDocument} from './schemas/mention';
import type {ChecklistItemDocument} from './schemas/checklistItem';
import type {PullRequestDocument} from './schemas/pullRequest';
import type {ChecklistDocument} from './schemas/checklist';
import type {ReactionDocument} from './schemas/reaction';
import type {ActivityDocument} from './schemas/activity';
import type {MemberDocument} from './schemas/member';
import type {StatusDocument} from './schemas/status';
import type {ProjectDocument} from './schemas/project';
import type {NotificationDocument} from './schemas/notification';
import type {ReminderDocument} from './schemas/reminder';
import type {CommentDocument} from './schemas/comment';
import type {TaskLabelDocument} from './schemas/taskLabel';
import type {LabelDocument} from './schemas/label';
import type {LabelGroupDocument} from './schemas/labelGroup';
import type {TaskDocument} from './schemas/task';
import type {TaskContentDocument} from './schemas/taskContent';
import type { WorkItemTypeDocument } from './schemas/workItemType'
import type { SavedFilterDocument } from './schemas/savedFilter'
import type { StarredDocument } from './schemas/starred'
import type { CustomFieldDefinitionDocument } from './schemas/customFieldDefinition'
import type { CustomFieldTabDocument } from './schemas/customFieldTab'
import type { UserPreferencesDocument } from './schemas/userPreferences'
import type { CustomFieldGroupDocument } from './schemas/customFieldGroup'
import type { CustomFieldValueDocument } from './schemas/customFieldValue'
import type { CycleDocument } from './schemas/cycle'
import type { CycleWorkItemDocument } from './schemas/cycleWorkItem'
import type { PersonalNoteDocument } from './schemas/personalNote'
import type { PersonalNoteContentDocument } from './schemas/personalNoteContent'
import type { SpaceOrderDocument } from './schemas/spaceOrder'
import type { EntityWatcherDocument } from './schemas/entityWatcher'
import type { DocumentContributorDocument } from './schemas/documentContributor'
import type { RxCollection, RxDatabase } from 'rxdb'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

setPremiumFlag()

// Add plugins
if (import.meta.env.DEV) {
  addRxPlugin(RxDBDevModePlugin)
}
addRxPlugin(RxDBCleanupPlugin)
addRxPlugin(RxDBUpdatePlugin)
addRxPlugin(RxDBQueryBuilderPlugin)
addRxPlugin(RxDBMigrationSchemaPlugin)
addRxPlugin(RxDBLeaderElectionPlugin)

// Type definitions
export type TaskCollection = RxCollection<TaskDocument>
export type TaskContentCollection = RxCollection<TaskContentDocument>
export type LabelCollection = RxCollection<LabelDocument>
export type LabelGroupCollection = RxCollection<LabelGroupDocument>
export type TaskLabelCollection = RxCollection<TaskLabelDocument>
export type CommentCollection = RxCollection<CommentDocument>
export type ReminderCollection = RxCollection<ReminderDocument>
export type NotificationCollection = RxCollection<NotificationDocument>
export type ProjectCollection = RxCollection<ProjectDocument>
export type StatusCollection = RxCollection<StatusDocument>
export type MemberCollection = RxCollection<MemberDocument>
export type ActivityCollection = RxCollection<ActivityDocument>
export type ReactionCollection = RxCollection<ReactionDocument>
export type ChecklistCollection = RxCollection<ChecklistDocument>
export type ChecklistItemCollection = RxCollection<ChecklistItemDocument>
export type PullRequestCollection = RxCollection<PullRequestDocument>
export type MentionCollection = RxCollection<MentionDocument>
export type TaskRelationCollection = RxCollection<TaskRelationDocument>
export type PageCollection = RxCollection<PageDocument>
export type PageContentCollection = RxCollection<PageContentDocument>
export type AttachmentCollection = RxCollection<AttachmentDocument>
export type SubscriptionCollection = RxCollection<SubscriptionDocument>
export type PlanCollection = RxCollection<PlanDocument>
export type UsageCollection = RxCollection<UsageDocument>
export type WorkItemTypeCollection = RxCollection<WorkItemTypeDocument>
export type EntityLinkCollection = RxCollection<EntityLinkDocument>
export type SavedFilterCollection = RxCollection<SavedFilterDocument>
export type CustomFieldDefinitionCollection = RxCollection<CustomFieldDefinitionDocument>
export type CustomFieldTabCollection = RxCollection<CustomFieldTabDocument>
export type UserPreferencesCollection = RxCollection<UserPreferencesDocument>
export type CustomFieldGroupCollection = RxCollection<CustomFieldGroupDocument>
export type CustomFieldValueCollection = RxCollection<CustomFieldValueDocument>
export type StarredCollection = RxCollection<StarredDocument>
export type CycleCollection = RxCollection<CycleDocument>
export type CycleWorkItemCollection = RxCollection<CycleWorkItemDocument>
export type PersonalNoteCollection = RxCollection<PersonalNoteDocument>
export type PersonalNoteContentCollection = RxCollection<PersonalNoteContentDocument>
export type SpaceOrderCollection = RxCollection<SpaceOrderDocument>
export type EntityWatcherCollection = RxCollection<EntityWatcherDocument>
export type DocumentContributorCollection = RxCollection<DocumentContributorDocument>

export type MolyDatabaseCollections = {
  tasks: TaskCollection
  taskcontent: TaskContentCollection
  labels: LabelCollection
  labelgroups: LabelGroupCollection
  tasklabels: TaskLabelCollection
  comments: CommentCollection
  reminders: ReminderCollection
  notifications: NotificationCollection
  projects: ProjectCollection
  statuses: StatusCollection
  members: MemberCollection
  activities: ActivityCollection
  reactions: ReactionCollection
  checklists: ChecklistCollection
  checklistitems: ChecklistItemCollection
  workitempullrequests: PullRequestCollection
  mentions: MentionCollection
  taskrelations: TaskRelationCollection
  pages: PageCollection
  pagecontent: PageContentCollection
  attachments: AttachmentCollection
  subscriptions: SubscriptionCollection
  plans: PlanCollection
  usage: UsageCollection
  workitemtypes: WorkItemTypeCollection
  entitylinks: EntityLinkCollection
  savedfilters: SavedFilterCollection
  customfielddefinitions: CustomFieldDefinitionCollection
  customfieldtabs: CustomFieldTabCollection
  customfieldgroups: CustomFieldGroupCollection
  customfieldvalues: CustomFieldValueCollection
  starred: StarredCollection
  cycles: CycleCollection
  cycleworkitems: CycleWorkItemCollection
  personalnotes: PersonalNoteCollection
  personalnotecontents: PersonalNoteContentCollection
  spaceorders: SpaceOrderCollection
  entitywatchers: EntityWatcherCollection
  documentcontributors: DocumentContributorCollection
  userpreferences: UserPreferencesCollection
}

export type MolyDatabase = RxDatabase<MolyDatabaseCollections>

export type {
  TaskDocument,
  TaskContentDocument,
  LabelDocument,
  LabelGroupDocument,
  TaskLabelDocument,
  CommentDocument,
  ReminderDocument,
  NotificationDocument,
  ProjectDocument,
  StatusDocument,
  MemberDocument,
  ActivityDocument,
  ReactionDocument,
  ChecklistDocument,
  ChecklistItemDocument,
  PullRequestDocument,
  MentionDocument,
  TaskRelationDocument,
  PageDocument,
  PageContentDocument,
  AttachmentDocument,
  WorkItemTypeDocument,
  EntityLinkDocument,
  SavedFilterDocument,
  CustomFieldDefinitionDocument,
  CustomFieldTabDocument,
  CustomFieldGroupDocument,
  CustomFieldValueDocument,
  StarredDocument,
  CycleDocument,
  CycleWorkItemDocument,
  PersonalNoteDocument,
  PersonalNoteContentDocument,
  SpaceOrderDocument,
  EntityWatcherDocument,
  DocumentContributorDocument,
  UserPreferencesDocument,
}

// Singleton instance
let dbInstance: MolyDatabase | null = null
const replicationStates: Map<string, any> = new Map()
let initializationPromise: Promise<MolyDatabase> | null = null
let purgeServiceInstance: PurgeService | null = null
let backgroundReplicationAbort: AbortController | null = null
let leadershipPromise: Promise<void> | null = null
let replicationManager: ReplicationManager | null = null
let isLeaderTab = false
const leadershipListeners: Set<(isLeader: boolean) => void> = new Set()

/**
 * Returns a promise that resolves when THIS tab becomes the RxDB leader.
 * Followers receive this promise but only see it resolve if/when they take
 * over leadership (e.g. because the current leader tab closed).
 */
export const getLeadershipPromise = (): Promise<void> | null => leadershipPromise

/**
 * Check if the current tab currently holds leadership.
 */
export const getIsLeaderTab = (): boolean => isLeaderTab

/**
 * Subscribe to leadership transitions. Invoked with true when this tab
 * becomes leader. Returns an unsubscribe function.
 */
export const subscribeLeadership = (listener: (isLeader: boolean) => void): (() => void) => {
  leadershipListeners.add(listener)
  if (isLeaderTab) listener(true)
  return () => {
    leadershipListeners.delete(listener)
  }
}

/**
 * Returns true when no replication has in-flight push or pull work. Used by
 * the Playwright suite (`window.__moly_sync_idle`) to wait deterministically
 * for RxDB to catch up after a mutation, instead of sleeping.
 */
export const getRxDBSyncIdle = (): boolean => {
  for (const [, state] of replicationStates.entries()) {
    const active = state?.active$
    if (active && typeof active.getValue === 'function') {
      if (active.getValue()) return false
    } else if (active && 'value' in active) {
      if (active.value) return false
    }
  }
  return true
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  Object.defineProperty(window, '__moly_sync_idle', {
    value: () => getRxDBSyncIdle(),
    configurable: true,
  })
}

const notifyLeadership = (leader: boolean) => {
  isLeaderTab = leader
  for (const listener of leadershipListeners) {
    try {
      listener(leader)
    } catch (err) {
      logger.logError(err as Error, LogCategory.DATABASE_INIT, {
        message: 'Leadership listener threw',
      })
    }
  }
}

/**
 * Initialize RxDB database for a specific environment.
 *
 * `userId` is required so per-user collections (e.g. userpreferences) can
 * filter the workspace-scoped WebSocket broadcast to the current user.
 */
export const initRxDB = async (
  envId: string,
  userId: string,
): Promise<MolyDatabase> => {
  // Return existing instance if already initialized for this environment
  const dbName = `moly_${envId}`

  if (dbInstance && dbInstance.name === dbName) {
    return dbInstance
  }

  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    return initializationPromise
  }

  // Close existing database ONLY if different workspace
  if (dbInstance && dbInstance.name !== dbName) {
    await closeRxDB()
  } else if (dbInstance) {
    return dbInstance
  }

  // Create a promise for this initialization
  initializationPromise = (async () => {
    const startTime = performance.now()
    try {
      logger.info('[RxDB] Starting initialization', { dbName, envId })
      logger.logEvent(LogCategory.DATABASE_INIT, 'start', {
        dbName,
        envId,
      })

      // Handle any pending database resets before initialization
      logger.logDebug('[RxDB] Handling pending resets')
      await handlePendingResets()
      logger.logDebug('[RxDB] Pending resets handled')

      // Create database + add collections, with one-shot DB6 recovery in dev.
      //
      // DB6 = "schema hash does not match the schema stored in the internal
      // database" — i.e. the stored schema and the schema in code disagree
      // under the same `version` field. This happens during iterative schema
      // development (especially mid-launch) when a property changes without a
      // version bump. Pre-launch the right move is to wipe local IndexedDB
      // and let it re-pull from the server. Bounded to one retry to avoid
      // infinite loops on genuine bugs.
      const createDb = () => {
        const workerStorage = getRxStorageWorker({
          workerInput: () => new Worker(
            new URL('./rxdb.worker.ts', import.meta.url),
            { type: 'module' }
          ),
        })
        return createRxDatabase<MolyDatabaseCollections>({
          name: dbName,
          // Validation runs inside the worker (see rxdb.worker.ts), so in
          // production the main thread consumes the worker storage directly —
          // wrapping it again here would validate every write twice, off the
          // worker. In DEV the dev-mode plugin's DVM1 check inspects only the
          // top-level storage and the worker proxy reports its name as
          // "remote", so dev-mode can't see the in-worker validator. Wrap on
          // the main thread in DEV only to satisfy that check.
          storage: import.meta.env.DEV
            ? wrappedValidateAjvStorage({ storage: workerStorage })
            : workerStorage,
          multiInstance: true, // Enable cross-tab sync
          eventReduce: true, // Performance optimization
          ...(import.meta.env.DEV && { ignoreDuplicate: true }), // Allow reopening existing database (dev-mode only)
          cleanupPolicy: {
            // Auto-cleanup old document revisions
            minimumDeletedTime: 1000 * 60 * 60 * 24 * 30, // 30 days
            minimumCollectionAge: 1000 * 60, // 1 minute
            runEach: 1000 * 60 * 5, // Every 5 minutes
            awaitReplicationsInSync: true,
            waitForLeadership: true,
          },
        })
      }

      logger.logDebug('[RxDB] Creating database')
      let db = await createDb()
      logger.logDebug('[RxDB] Database created successfully')

      let db6Recovered = false
       
      while (true) {
        try {
          logger.logDebug('[RxDB] Adding collections')
          await db.addCollections({
        tasks: {
          schema: taskSchema,
          migrationStrategies: taskMigrationStrategies,
        },
        taskcontent: {
          schema: taskContentSchema,
          migrationStrategies: taskContentMigrationStrategies,
        },
        labels: { schema: labelSchema },
        labelgroups: { schema: labelGroupSchema },
        tasklabels: { schema: taskLabelSchema },
        comments: {
          schema: commentSchema,
          migrationStrategies: commentMigrationStrategies,
        },
        reminders: { schema: reminderSchema },
        notifications: { schema: notificationSchema, migrationStrategies: notificationMigrationStrategies },
        projects: {
          schema: projectSchema,
          migrationStrategies: projectMigrationStrategies,
        },
        statuses: {
          schema: statusSchema,
          migrationStrategies: statusMigrationStrategies,
        },
        members: {
          schema: memberSchema,
          migrationStrategies: memberMigrationStrategies,
        },
        activities: {
          schema: activitySchema,
          migrationStrategies: activityMigrationStrategies,
        },
        reactions: { schema: reactionSchema, migrationStrategies: reactionMigrationStrategies },
        checklists: {
          schema: checklistSchema,
          migrationStrategies: checklistMigrationStrategies,
        },
        checklistitems: {
          schema: checklistItemSchema,
          migrationStrategies: checklistItemMigrationStrategies,
        },
        workitempullrequests: { schema: pullRequestSchema, migrationStrategies: pullRequestMigrationStrategies },
        mentions: { schema: mentionSchema, migrationStrategies: mentionMigrationStrategies },
        taskrelations: { schema: taskRelationSchema },
        pages: { schema: pageSchema },
        pagecontent: { schema: pageContentSchema },
        attachments: { schema: attachmentSchema },
        subscriptions: {
          schema: subscriptionSchema,
          migrationStrategies: {
            // v0 → v1: add Paddle columns + billingProvider routing key.
            // Existing local docs were created on Stripe so default
            // billingProvider='stripe' and leave Paddle fields null.
            1: (oldDoc) => ({
              ...oldDoc,
              paddleCustomerId: null,
              paddleSubscriptionId: null,
              billingProvider: 'stripe',
            }),
          },
        },
        plans: { schema: planSchema },
        usage: { schema: usageSchema },
        workitemtypes: {
          schema: workItemTypeSchema,
          migrationStrategies: workItemTypeMigrationStrategies,
        },
        entitylinks: { schema: entityLinkSchema },
        savedfilters: {
          schema: savedFilterSchema,
          migrationStrategies: savedFilterMigrationStrategies,
        },
        customfielddefinitions: {
          schema: customFieldDefinitionSchema,
          migrationStrategies: customFieldDefinitionMigrationStrategies,
        },
        customfieldtabs: {
          schema: customFieldTabSchema,
          migrationStrategies: customFieldTabMigrationStrategies,
        },
        customfieldgroups: {
          schema: customFieldGroupSchema,
          migrationStrategies: customFieldGroupMigrationStrategies,
        },
        customfieldvalues: {
          schema: customFieldValueSchema,
          migrationStrategies: customFieldValueMigrationStrategies,
        },
        starred: { schema: starredSchema },
        cycles: { schema: cycleSchema },
        cycleworkitems: {
          schema: cycleWorkItemSchema,
          migrationStrategies: cycleWorkItemMigrationStrategies,
        },
        personalnotes: { schema: personalNoteSchema },
        personalnotecontents: { schema: personalNoteContentSchema },
        spaceorders: { schema: spaceOrderSchema },
        entitywatchers: { schema: entityWatcherSchema },
        documentcontributors: { schema: documentContributorSchema },
        userpreferences: {
          schema: userPreferencesSchema,
          migrationStrategies: userPreferencesMigrationStrategies,
        },
      })
          logger.logDebug('[RxDB] Collections added')
          break
        } catch (err: any) {
          const msg = err instanceof Error ? err.message : String(err)
          if (msg.includes('DB6') && !db6Recovered && import.meta.env.DEV) {
            db6Recovered = true
            logger.logError(
              new Error('[RxDB] DB6 schema-hash mismatch — wiping local IndexedDB and retrying once (dev-only recovery for pre-launch schema iteration)'),
              LogCategory.DATABASE_INIT,
              {
                dbName,
                hint: 'If this keeps firing, a schema property changed without bumping `version`.',
              },
            )
            try { await db.remove() } catch { /* ignore */ }
            try { await deleteAllRxDBDatabases() } catch { /* ignore */ }
            db = await createDb()
            continue
          }
          throw err
        }
      }

      dbInstance = db

      // Tier 1 (Critical): Blocks UI -- minimum data to render sidebar + task lists
      // Tier 2 (Important): Starts in background -- enrichment data (labels, comments, etc.)
      // Tier 3 (Deferred): Starts after Tier 2 -- detail views, features, analytics
      type ReplicationTask = {
        key: string
        label: string
        tier: 1 | 2 | 3
        setup?: () => Promise<any>
        skipMessage?: string
      }

      const replicationTasks: Array<ReplicationTask> = [
        // --- Tier 1: Critical (blocks UI) ---
        {
          key: 'tasks',
          label: 'tasks',
          tier: 1,
          setup: () => setupTaskReplication(db.tasks, envId),
        },
        {
          key: 'projects',
          label: 'projects',
          tier: 1,
          setup: () => setupProjectReplication(db.projects, envId),
        },
        {
          key: 'statuses',
          label: 'statuses',
          tier: 1,
          setup: () => setupStatusReplication(db.statuses, envId),
        },
        {
          key: 'members',
          label: 'members',
          tier: 1,
          setup: () => setupMemberReplication(db.members, envId),
        },
        {
          key: 'notifications',
          label: 'notifications',
          tier: 1,
          setup: () => setupNotificationReplication(db.notifications, envId),
        },
        {
          key: 'userpreferences',
          label: 'userpreferences',
          tier: 1,
          setup: () => setupUserPreferencesReplication(db.userpreferences, envId, userId),
        },
        // --- Tier 2: Important (background, no block) ---
        {
          key: 'labels',
          label: 'labels',
          tier: 2,
          setup: () => setupLabelReplication(db.labels, envId),
        },
        {
          key: 'labelgroups',
          label: 'labelgroups',
          tier: 2,
          setup: () => setupLabelGroupReplication(db.labelgroups, envId),
        },
        {
          key: 'tasklabels',
          label: 'tasklabels',
          tier: 2,
          setup: () => setupTaskLabelReplication(db.tasklabels, envId),
        },
        {
          key: 'comments',
          label: 'comments',
          tier: 2,
          setup: () => setupCommentReplication(db.comments, envId),
        },
        {
          key: 'reminders',
          label: 'reminders',
          tier: 2,
          setup: () => setupReminderReplication(db.reminders, envId),
        },
        {
          key: 'workitemtypes',
          label: 'workitemtypes',
          tier: 2,
          setup: () => setupWorkItemTypeReplication(db.workitemtypes, envId),
        },
        {
          key: 'subscriptions',
          label: 'subscriptions',
          tier: 2,
          setup: () => setupSubscriptionReplication(db.subscriptions, envId),
        },
        {
          key: 'cycles',
          label: 'cycles',
          tier: 2,
          setup: () => setupCycleReplication(db.cycles, envId),
        },
        {
          key: 'cycleworkitems',
          label: 'cycleworkitems',
          tier: 2,
          setup: () => setupCycleWorkItemReplication(db.cycleworkitems, envId),
        },
        {
          key: 'personalnotes',
          label: 'personalnotes',
          tier: 2,
          setup: () => setupPersonalNoteReplication(db.personalnotes, envId, userId),
        },
        {
          key: 'personalnotecontents',
          label: 'personalnotecontents',
          tier: 2,
          setup: () => setupPersonalNoteContentReplication(db.personalnotecontents, envId),
        },
        {
          // Tier 2 alongside projects: the sidebar falls back to A–Z until these
          // land, so a late arrival re-sorts Spaces under the user rather than
          // blocking first paint.
          key: 'spaceorders',
          label: 'spaceorders',
          tier: 2,
          setup: () => setupSpaceOrderReplication(db.spaceorders, envId, userId),
        },
        {
          key: 'plans',
          label: 'plans',
          tier: 2,
          setup: () => setupPlanReplication(db.plans),
        },
        // --- Tier 3: Deferred (background, after Tier 2) ---
        {
          key: 'taskcontent',
          label: 'taskcontent',
          tier: 3,
          setup: () => setupTaskContentReplication(db.taskcontent, envId),
        },
        {
          key: 'activities',
          label: 'activities',
          tier: 3,
          setup: () => setupActivityReplication(db.activities, envId),
        },
        {
          key: 'reactions',
          label: 'reactions',
          tier: 3,
          setup: () => setupReactionReplication(db.reactions, envId),
        },
        {
          key: 'checklists',
          label: 'checklists',
          tier: 3,
          setup: () => setupChecklistReplication(db.checklists, envId),
        },
        {
          key: 'checklistitems',
          label: 'checklistitems',
          tier: 3,
          setup: () => setupChecklistItemReplication(db.checklistitems, envId),
        },
        {
          key: 'workitempullrequests',
          label: 'workitempullrequests',
          tier: 3,
          setup: () => setupPullRequestReplication(db.workitempullrequests, envId),
        },
        {
          key: 'mentions',
          label: 'mentions',
          tier: 3,
          setup: () => setupMentionReplication(db.mentions, envId),
        },
        {
          key: 'taskrelations',
          label: 'taskrelations',
          tier: 3,
          setup: () => setupTaskRelationReplication(db.taskrelations, envId),
        },
        {
          key: 'pages',
          label: 'pages',
          tier: 3,
          setup: () => setupPageReplication(db.pages, envId),
        },
        {
          key: 'pagecontent',
          label: 'pagecontent',
          tier: 3,
          setup: () => setupPageContentReplication(db.pagecontent, envId),
        },
        {
          key: 'attachments',
          label: 'attachments',
          tier: 3,
          setup: () => setupAttachmentReplication(db.attachments, envId),
        },
        {
          key: 'usage',
          label: 'usage',
          tier: 3,
          setup: () => setupUsageReplication(db.usage, envId),
        },
        {
          key: 'entitylinks',
          label: 'entitylinks',
          tier: 3,
          setup: () => setupEntityLinkReplication(db.entitylinks, envId),
        },
        {
          key: 'savedfilters',
          label: 'savedfilters',
          tier: 3,
          setup: () => setupSavedFilterReplication(db.savedfilters, envId),
        },
        {
          key: 'customfieldtabs',
          label: 'customfieldtabs',
          tier: 3,
          setup: () => setupCustomFieldTabReplication(db.customfieldtabs, envId),
        },
        {
          key: 'customfieldgroups',
          label: 'customfieldgroups',
          tier: 3,
          setup: () => setupCustomFieldGroupReplication(db.customfieldgroups, envId),
        },
        {
          key: 'customfielddefinitions',
          label: 'customfielddefinitions',
          tier: 3,
          setup: () => setupCustomFieldDefinitionReplication(db.customfielddefinitions, envId),
        },
        {
          key: 'customfieldvalues',
          label: 'customfieldvalues',
          tier: 3,
          setup: () => setupCustomFieldValueReplication(db.customfieldvalues, envId),
        },
        {
          key: 'starred',
          label: 'starred',
          tier: 3,
          setup: () => setupStarredReplication(db.starred, envId),
        },
        {
          key: 'entitywatchers',
          label: 'entitywatchers',
          tier: 3,
          setup: () => setupEntityWatcherReplication(db.entitywatchers, envId),
        },
        {
          key: 'documentcontributors',
          label: 'documentcontributors',
          tier: 3,
          setup: () => setupDocumentContributorReplication(db.documentcontributors, envId),
        },
      ]

      // Build a single replication manager. Followers do NOT run any of
      // this -- they read from the shared IndexedDB via RxDB multiInstance
      // broadcast. Only the leader tab performs pulls/pushes, so two tabs
      // no longer contend on IndexedDB writes or duplicate network work.
      replicationManager = new ReplicationManager(db, replicationTasks, replicationStates)

      // Used by closeRxDB to cancel any pending leadership-gated work if the
      // tab tears down before ever becoming leader.
      backgroundReplicationAbort = new AbortController()
      const abortSignal = backgroundReplicationAbort.signal

      leadershipPromise = (async () => {
        await db.waitForLeadership()
        if (abortSignal.aborted) return
        logger.info('[RxDB] This tab is now leader -- starting replication and purge')
        notifyLeadership(true)

        try {
          await replicationManager!.start()
        } catch (err) {
          if (!abortSignal.aborted) {
            logger.logError(err as Error, LogCategory.SYNC, {
              phase: 'tier1',
              message: 'Leader replication failed to start',
            })
          }
        }

        if (abortSignal.aborted) return
        // Only the leader runs purge -- prevents two tabs from purging
        // concurrently and matches cleanupPolicy.waitForLeadership.
        purgeServiceInstance = new PurgeService(db, envId)
        purgeServiceInstance.start().catch((err) => {
          logger.logError(err as Error, LogCategory.DATABASE_INIT, {
            message: 'Purge service failed to start',
          })
        })
      })()

      leadershipPromise.catch((err) => {
        if (!abortSignal.aborted) {
          logger.logError(err as Error, LogCategory.DATABASE_INIT, {
            message: 'Leadership chain failed',
          })
        }
      })

      const duration = performance.now() - startTime
      logger.logEvent(LogCategory.DATABASE_INIT, 'complete', {
        dbName,
        duration,
        collectionsCount: replicationTasks.length,
      })
      logger.info(`[RxDB] Initialization completed`, {
        duration: `${duration.toFixed(2)}ms`,
        dbName,
        collectionsCount: replicationTasks.length,
      })

      return db
    } catch (error) {
      const duration = performance.now() - startTime
      logger.logError(error as Error, LogCategory.DATABASE_INIT, {
        dbName,
        envId,
        duration,
      })
      throw error
    } finally {
      // Clear the initialization promise when done
      initializationPromise = null
    }
  })()

  return initializationPromise
}

/**
 * Get current database instance
 */
export const getRxDB = (): MolyDatabase => {
  if (!dbInstance) {
    throw new Error('RxDB not initialized. Call initRxDB first.')
  }
  return dbInstance
}

/**
 * Close database and cleanup.
 *
 * `remove` (default true) deletes the IndexedDB databases as well. Pass false
 * when another tab is doing the deletion (cross-tab logout) so we don't race
 * its `deleteDatabase` with our own.
 */
export const closeRxDB = async ({ remove = true }: { remove?: boolean } = {}) => {
  // Abort any pending leadership-gated work so it stops registering new states
  if (backgroundReplicationAbort) {
    backgroundReplicationAbort.abort()
    backgroundReplicationAbort = null
  }

  // Stop purge service
  if (purgeServiceInstance) {
    purgeServiceInstance.stop()
    purgeServiceInstance = null
    logger.info('[RxDB] Purge service stopped')
  }

  // Cancel all replications via manager (falls back to iterating the map
  // if the manager was never created, e.g. tab tore down as a follower).
  if (replicationManager) {
    await replicationManager.stop()
    replicationManager = null
  } else {
    for (const [_name, replication] of replicationStates.entries()) {
      await replication.cancel()
    }
    replicationStates.clear()
  }

  // Reset leadership state so a re-init on a different workspace starts fresh
  leadershipPromise = null
  notifyLeadership(false)

  // Close shared WebSocket connection
  const { closeSharedWebSocket } = await import('./utils/sharedWebSocketManager')
  closeSharedWebSocket()

  // Close database
  if (dbInstance) {
    if (remove) {
      await dbInstance.remove()
    } else {
      await dbInstance.close()
    }
    dbInstance = null
  }
}

/**
 * Get replication state for monitoring
 */
export const getReplicationState = (collectionName: string) => {
  return replicationStates.get(collectionName)
}

/**
 * Get purge service instance for monitoring or manual operations
 */
export const getPurgeService = (): PurgeService | null => {
  return purgeServiceInstance
}
